import { useTranslation } from 'react-i18next';

import { useModelContext } from '../../context/modelContext';
import { formatMetersPerUnit } from './modelScaleUtils';

interface ModelScaleSummaryRowProps {
  /** Called when the user clicks the edit button — parent reopens the bottom scale toolbar. */
  onEdit: () => void;
  /** When true, the edit button is disabled (e.g. no model uploaded yet). */
  disabled?: boolean;
}

/**
 * Compact row rendered in the SetupProject sidebar below "Maintenance / Units".
 * Always visible: the default "1 unit = 1.000 m" is shown from the moment the
 * page opens, with the edit button disabled until a file is actually uploaded.
 */
export function ModelScaleSummaryRow({ onEdit, disabled = false }: ModelScaleSummaryRowProps) {
  const { t } = useTranslation();
  const { scaleCalibrated, metersPerUnit } = useModelContext();

  return (
    <div className="mt-4 px-6 w-full">
      <label className="flex items-center text-xs text-white mb-1">
        {t('projects.newProject.modelScale.title', 'Model scale')}
      </label>
      <div className="flex items-center gap-2">
        <div className="flex-1 h-[36px] flex items-center justify-between rounded-lg bg-black border border-black px-4">
          <span className="text-[11px] uppercase tracking-wider text-gray-500">
            {t('projects.newProject.modelScale.currentLabel', '1 unit =')}
          </span>
          <span className={`text-sm font-mono font-semibold ${scaleCalibrated ? 'text-lime-400' : 'text-gray-300'}`}>
            {formatMetersPerUnit(metersPerUnit)}
          </span>
        </div>
        <button
          type="button"
          onClick={onEdit}
          disabled={disabled}
          className={`h-[36px] w-[36px] flex items-center justify-center rounded-lg bg-black border border-black transition-colors ${
            disabled
              ? 'opacity-40 cursor-not-allowed text-gray-500'
              : 'hover:border-lime-400/50 text-gray-300 hover:text-lime-400'
          }`}
          aria-label={t('common.edit', 'Edit')}
          title={
            disabled
              ? t('projects.newProject.modelScale.uploadFirst', 'Upload a model first')
              : t('common.edit', 'Edit')
          }
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
          </svg>
        </button>
      </div>
    </div>
  );
}
